import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { Error } from '../../models/error.model';
import { Company } from '../../models/company.model';
import { StorageCenter } from '../../models/storage.center.model';
import { GlobalSwal } from '../../styles/swal.style';
import { StorageCenterDA } from '../../services/admin/storage.center.service';
import { modalHide } from '../../utils/modal.util';
import { legendInvalid, legendIconInvalid, legendValidInvalidRestart, legendIconValidInvalidRestart } from '../../utils/legend.data.util';
import { errorWebHandler } from '../../scripts/error.web.handler.script';

import AppLoading from '../Loading';
import AppStorageCenterForm from '../../interfaces/main/inventory/storage/component/StorageCenterForm';

export interface AppModalStorageCenterUpdateProps {
  companyForUser: Company | null,
  storageCentersForCompany: StorageCenter[] | null,
  storageCenter: StorageCenter | null,
  storageCenters?: StorageCenter[] | null,
  setStorageCenter: Dispatch<SetStateAction<StorageCenter | null>>,
  setStorageCenters?: Dispatch<SetStateAction<StorageCenter[] | null>>,
  loadStorageCentersForCompany: Function
};

let errorResponse: Error, storageCenterResponse: StorageCenter;

const AppModalStorageCenterUpdate: React.FunctionComponent<AppModalStorageCenterUpdateProps> = ({companyForUser, storageCentersForCompany, storageCenter, storageCenters, setStorageCenter, setStorageCenters, loadStorageCentersForCompany}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [name, setName] = useState({value: '', valid: false})
  const [street, setStreet] = useState({value: '', valid: false})
  const [streetNumber, setStreetNumber] = useState({value: '', valid: false})
  const [additional, setAdditional] = useState({value: '', valid: false})
  const [administrativeArea, setAdministrativeArea] = useState({value: '', valid: false})
  const [city, setCity] = useState({value: '', valid: false})
  const [zipCode, setZipCode] = useState({value: '', valid: false})
  const [country, setCountry] = useState({value: '', valid: false})
  const [isSav, setIsSav] = useState({value: false, valid: true})

  const submitStorageCenterUpdate = async () => {
    setLoadIndicator('on')

    if (companyForUser && storageCentersForCompany && storageCenter && name.valid && street.valid && streetNumber.valid && additional.valid && administrativeArea.valid && city.valid && zipCode.valid && country.valid) {
      await StorageCenterDA.putStorageCenter(companyForUser.id, storageCenter.id, name.value, street.value, streetNumber.value, additional.value, administrativeArea.value, city.value, zipCode.value, country.value, isSav.value).then( (response) => {
        if (response.status === 200) {
          storageCenterResponse = response.data

          if (storageCenters && setStorageCenters) {
            let tempStorageCenters = storageCenters.map((temp_item) => {
              if (temp_item.id === storageCenter.id) {
                temp_item = storageCenterResponse
              }
              return temp_item
            })
            setStorageCenters(tempStorageCenters)
          } else {
            setStorageCenter(storageCenterResponse)
          }

          loadStorageCentersForCompany(companyForUser.id)

          GlobalSwal.fire({
            title: lang.labels.successfullyUpdatedStorageCenter,
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            executeHideModalStorageCenterUpdate()
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: `${errorResponse.message}!`,
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!name.value.length) legendIconInvalid('modal-storage-center-update-input-name', 'modal-storage-center-update-legend-name-required')
        if (!street.value.length) legendIconInvalid('modal-storage-center-update-input-street', 'modal-storage-center-update-legend-street-required')
        if (!administrativeArea.value.length) legendIconInvalid('modal-storage-center-update-input-administrative-area', 'modal-storage-center-update-legend-administrative-area-required')
        if (!city.value.length) legendIconInvalid('modal-storage-center-update-input-city', 'modal-storage-center-update-legend-city-required')
        if (!country.value.length) legendInvalid('modal-storage-center-update-legend-country-required')
        setLoadIndicator('off')
      })
    }
  }

  const executeHideModalStorageCenterUpdate = () => {
    modalHide('modal-storage-center-update')

    setTimeout( () => {
      restartModal()
    }, 200 )
  }

  function restartModal() {
    legendIconValidInvalidRestart('modal-storage-center-update-input-name', 'modal-storage-center-update-legend-name-valid')
    legendIconValidInvalidRestart('modal-storage-center-update-input-name', 'modal-storage-center-update-legend-name-required')
    legendIconValidInvalidRestart('modal-storage-center-update-input-street', 'modal-storage-center-update-legend-street-valid')
    legendIconValidInvalidRestart('modal-storage-center-update-input-street', 'modal-storage-center-update-legend-street-required')
    legendIconValidInvalidRestart('modal-storage-center-update-input-street-number', 'modal-storage-center-update-legend-street-number-valid')
    legendIconValidInvalidRestart('modal-storage-center-update-input-additional', 'modal-storage-center-update-legend-additional-valid')
    legendIconValidInvalidRestart('modal-storage-center-update-input-administrative-area', 'modal-storage-center-update-legend-administrative-area-valid')
    legendIconValidInvalidRestart('modal-storage-center-update-input-administrative-area', 'modal-storage-center-update-legend-administrative-area-required')
    legendIconValidInvalidRestart('modal-storage-center-update-input-city', 'modal-storage-center-update-legend-city-valid')
    legendIconValidInvalidRestart('modal-storage-center-update-input-city', 'modal-storage-center-update-legend-city-required')
    legendIconValidInvalidRestart('modal-storage-center-update-input-zip-code', 'modal-storage-center-update-legend-zip-code-valid')
    legendValidInvalidRestart('modal-storage-center-update-legend-country-required')

    if (storageCenters && setStorageCenters) {
      setStorageCenter(null)
    }
  }

  useEffect( () => {
    if (storageCenter) {
      setName({value: storageCenter.name, valid: true})
      setStreet({value: storageCenter.address.street, valid: true})
      setStreetNumber({value: storageCenter.address.number, valid: true})
      setAdditional({value: storageCenter.address.additional, valid: true})
      setAdministrativeArea({value: storageCenter.address.administrative_area, valid: true})
      setCity({value: storageCenter.address.city, valid: true})
      setZipCode({value: storageCenter.address.zip_code, valid: true})
      setCountry({value: storageCenter.address.country, valid: true})
      setIsSav({value: storageCenter.is_sav ?? false, valid: true})
    } else {
      setName({value: '', valid: false})
      setStreet({value: '', valid: false})
      setStreetNumber({value: '', valid: false})
      setAdditional({value: '', valid: false})
      setAdministrativeArea({value: '', valid: false})
      setCity({value: '', valid: false})
      setZipCode({value: '', valid: false})
      setIsSav({value: false, valid: true})

      setCountry({value: 'CH', valid: true})
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageCenter])

  return (
    <div id="modal-storage-center-update" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.updateStorageCenter}</h2>
          </div>
          <div className="modal-body">
            { companyForUser && storageCenter
              ?
              <AppStorageCenterForm component={"modal-storage-center-update"} storageCenter={storageCenter} name={name} street={street} streetNumber={streetNumber} additional={additional} administrativeArea={administrativeArea} city={city} zipCode={zipCode} country={country} isSav={isSav} setName={setName} setStreet={setStreet} setStreetNumber={setStreetNumber} setAdditional={setAdditional} setAdministrativeArea={setAdministrativeArea} setCity={setCity} setZipCode={setZipCode} setCountry={setCountry} setIsSav={setIsSav}></AppStorageCenterForm>
              :
              <AppLoading></AppLoading>
            }
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModalStorageCenterUpdate}>{lang.labels.discard}</button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitStorageCenterUpdate}>
              <span className="indicator-label">{lang.labels.update}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AppModalStorageCenterUpdate;
